import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useMotionValue, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { useStoryMode } from "../../hooks/useStoryMode";
import "./StorySlot.css";

/** How far a section has shrunk back by the time the next one covers it. */
const EXIT_SCALE = 0.8;
/** How small a section starts as it rises into view. */
const ENTER_SCALE = 0.86;
/** Fraction of the next section's approach by which this one has fully faded. */
const EXIT_FADE_BY = 0.55;
/** Opacity an arriving section starts from. */
const ENTER_OPACITY = 0.3;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeOut = (t: number) => 1 - (1 - t) * (1 - t);

interface StorySlotProps {
    children: ReactNode;
    first?: boolean;
    last?: boolean;
}

/**
 * One "slide" of the single page.
 *
 * Each section scrolls normally until its bottom edge reaches the bottom of the
 * viewport, then pins there while the next section rises up over it. As that
 * happens this one zooms back and fades out, and the arriving one zooms in from
 * slightly smaller — the page reads as moving from slide to slide rather than
 * as one long strip.
 *
 * Pinning by the bottom edge (a negative sticky `top` for sections taller than
 * the viewport) is what makes this safe for text-heavy sections: by the time a
 * section stops moving, all of it has already been scrolled past, so nothing
 * is ever covered before it could be read.
 *
 * The slot's extra height (one viewport) is cancelled by the next slot's
 * negative margin, so every section keeps exactly the document position it had
 * before. That matters: the navbar, ProgressRail and the hash jump all locate
 * sections by geometry, and the pin is released as soon as the next section
 * has taken over, so a section never lingers stuck where it doesn't belong.
 */
export default function StorySlot({ children, first = false, last = false }: StorySlotProps) {
    const on = useStoryMode();
    const slotRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState({ h: 0, vh: 0 });

    useLayoutEffect(() => {
        if (!on) return;
        const measure = () =>
            setSize({ h: stageRef.current?.offsetHeight ?? 0, vh: window.innerHeight });
        measure();
        const ro = new ResizeObserver(measure);
        if (stageRef.current) ro.observe(stageRef.current);
        window.addEventListener("resize", measure);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [on]);

    /** 0 → 1 as this section rises from the bottom of the viewport to its top. */
    const enter = useMotionValue(1);
    /** 0 → 1 as the next section rises over this one. */
    const exit = useMotionValue(0);

    const live = useRef({ on, size });
    live.current = { on, size };

    const update = useCallback(() => {
        if (!live.current.on) {
            enter.set(1);
            exit.set(0);
            return;
        }
        const vh = window.innerHeight;
        if (!first && stageRef.current) {
            enter.set(clamp01((vh - stageRef.current.getBoundingClientRect().top) / vh));
        }
        const next = last ? null : (slotRef.current?.nextElementSibling as HTMLElement | null);
        exit.set(next ? clamp01((vh - next.getBoundingClientRect().top) / vh) : 0);
    }, [enter, exit, first, last]);

    const { scrollY } = useScroll();
    useMotionValueEvent(scrollY, "change", update);
    useEffect(update, [update, on, size]);

    const scale = useTransform([enter, exit], ([e, x]: number[]) =>
        lerp(ENTER_SCALE, 1, easeOut(e)) * lerp(1, EXIT_SCALE, x)
    );
    const opacity = useTransform([enter, exit], ([e, x]: number[]) =>
        lerp(ENTER_OPACITY, 1, easeOut(e)) * (1 - clamp01(x / EXIT_FADE_BY))
    );
    /*
     * Zoom around the middle of whatever part of the section is actually on
     * screen: its top viewport-height while arriving, its bottom one while
     * leaving. Scaling a tall section around its true centre would swing the
     * visible part off toward an edge instead of receding straight back.
     */
    const originY = useTransform(exit, (x) => {
        const { h, vh } = live.current.size;
        if (h <= vh) return "50%";
        return x > 0 ? `${h - vh / 2}px` : `${vh / 2}px`;
    });
    // Once faded, stop catching clicks meant for the section arriving over it.
    const pointerEvents = useTransform(exit, (x) => (x > EXIT_FADE_BY ? "none" : "auto"));

    if (!on) {
        return <div ref={slotRef}>{children}</div>;
    }

    const { h, vh } = size;

    return (
        <div
            ref={slotRef}
            className="story-slot"
            style={{
                height: h ? h + (last ? 0 : vh) : undefined,
                marginTop: first ? 0 : -vh,
            }}
        >
            <motion.div
                ref={stageRef}
                className="story-stage"
                style={{
                    position: last ? "relative" : "sticky",
                    top: Math.min(0, vh - h),
                    scale,
                    opacity,
                    originY,
                    pointerEvents,
                }}
            >
                {children}
            </motion.div>
        </div>
    );
}
